// Sample / mock data for the Student Doubts Management module.
// This lets the College Web Portal render fully without a backend wired up.
// Replace with the GraphQL service layer (studentDoubtsService.js) once the
// backend endpoints are available.

// Staff-side status model (color-coded in the dashboard). Values mirror the
// backend `doubts_status` table:
//   Pending    -> orange  (new, not yet acknowledged)
//   Reviewing  -> blue     (faculty acknowledged, working on it)
//   Completed  -> green    (answer submitted)
//   Irrelevent -> slate    (not a valid / relevant doubt)
export const DOUBT_STATUS = {
  PENDING: "Pending",
  REVIEWING: "Reviewing",
  // Kept as SOLVED in code (the "answered" state); displayed as "Completed".
  SOLVED: "Completed",
  IRRELEVANT: "Irrelevent",
};

export const STATUS_COLOR = {
  // Pending is red — a new/unanswered doubt needs attention.
  [DOUBT_STATUS.PENDING]: "#ef4444",
  [DOUBT_STATUS.REVIEWING]: "#0ea5e9",
  [DOUBT_STATUS.SOLVED]: "#22c55e",
  [DOUBT_STATUS.IRRELEVANT]: "#94a3b8",
};

// Filter / categorization option lists (Story 5).
// Branch / class / section names mirror the real backend values shown in the
// filter dropdowns (e.g. branch "Ameerpet", class "Class1", section "SEC I-A").
export const BRANCHES = ["Ameerpet", "Dilsukhnagar", "Kukatpally", "Madhapur"];
export const CLASSES = ["Class1", "Class2"];
export const SECTIONS = ["SEC I-A", "SEC I-B", "SEC II-A", "SEC II-B"];
export const SUBJECTS = [
  "Operating Systems",
  "Physics",
  "Chemistry",
  "Mathematics",
  "Botany",
];
export const CATEGORIES = ["Conceptual", "Numerical", "Definition", "Diagram"];

// Chapters per subject (DB column `chapter`). Used by the chapter filter and the
// simulate-doubt form so a doubt can be tagged to a chapter, not just a subject.
export const SUBJECT_CHAPTER_MAP = {
  "Operating Systems": ["Process Management", "Deadlocks", "Memory Management", "File Systems"],
  Physics: ["Kinematics", "Thermodynamics", "Optics", "Electrostatics"],
  Chemistry: ["Chemical Bonding", "Thermochemistry", "Organic Basics", "Periodic Table"],
  Mathematics: ["Integration", "Differentiation", "Matrices", "Probability"],
  Botany: ["Plant Anatomy", "Photosynthesis", "Cell Biology", "Plant Kingdom"],
};

// Flat list of every chapter (for the multi-select when no subject is chosen).
export const CHAPTERS = [...new Set(Object.values(SUBJECT_CHAPTER_MAP).flat())];

// A Pending doubt older than this many hours is considered overdue (SLA breach).
export const SLA_HOURS = 4;

// True when a doubt has been waiting too long without being solved.
// Mirrors what `raised_time` + an SLA window would give us on the backend.
export const isOverdue = (d) => {
  if (!d || d.status === DOUBT_STATUS.SOLVED) return false;
  const raised = new Date(d.createdAt).getTime();
  return Date.now() - raised > SLA_HOURS * 3600 * 1000;
};

// Faculty & auto-routing rules (Story 5: Categorization & Assignment).
export const FACULTY = [
  "Dr. Ramesh Kumar",
  "Mrs. Sunitha Rao",
  "Mr. Anil Verma",
  "Ms. Priya Sharma",
];

// A doubt for a given subject is auto-routed to the mapped faculty member.
export const SUBJECT_FACULTY_MAP = {
  "Operating Systems": "Mr. Anil Verma",
  Physics: "Dr. Ramesh Kumar",
  Chemistry: "Mrs. Sunitha Rao",
  Mathematics: "Mr. Anil Verma",
  Botany: "Ms. Priya Sharma",
};

// Initials for the round avatar chip (e.g. "Sneha Patel" -> "SP").
export const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

// Compact "45m ago" style relative time. Accepts ISO date strings as well as
// epoch timestamps (the backend stores answered_time / timestamp as epoch
// seconds via getTimestamp(); an all-digit value is treated as epoch — seconds
// when 10 digits, milliseconds when 13).
export const timeAgo = (iso) => {
  if (!iso) return "";
  let then;
  const s = String(iso).trim();
  if (/^\d+$/.test(s)) {
    then = s.length <= 10 ? Number(s) * 1000 : Number(s);
  } else {
    then = new Date(s).getTime();
  }
  if (!then || isNaN(then)) return "";
  const diff = Math.max(0, Date.now() - then);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

// Absolute date + time (e.g. "7 Jul 2026, 3:45 PM") for chat bubbles, where the
// exact time is more useful than a relative "7h ago". Accepts the same epoch-or-ISO
// input as timeAgo.
export const formatDateTime = (iso) => {
  if (!iso) return "";
  let ms;
  const s = String(iso).trim();
  if (/^\d+$/.test(s)) {
    ms = s.length <= 10 ? Number(s) * 1000 : Number(s);
  } else {
    ms = new Date(s).getTime();
  }
  if (!ms || isNaN(ms)) return "";
  return new Date(ms).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

// Header stat cards (Total / Pending / Solved).
export const buildStats = (doubts) => {
  const count = (status) => doubts.filter((d) => d.status === status).length;
  return [
    { id: 1, label: "Total", counts: doubts.length },
    { id: 2, label: "Pending", counts: count(DOUBT_STATUS.PENDING) },
    { id: 3, label: "Solved", counts: count(DOUBT_STATUS.SOLVED) },
  ];
};

// A doubt image can be a real uploaded picture (data: URI or http URL) or,
// in this sample data, a generated SVG "photo" so it renders offline.
// `isImageSrc` lets the UI decide whether to <img> it or show a placeholder.
export const isImageSrc = (s) =>
  typeof s === "string" && (s.startsWith("data:") || /^https?:\/\//i.test(s));

const noteImage = (title, lines) =>
  "data:image/svg+xml;charset=utf-8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='360' height='240'>
      <rect width='100%' height='100%' fill='#fffdf5'/>
      <rect x='6' y='6' width='348' height='228' fill='none' stroke='#e2d9b8' stroke-width='2' rx='8'/>
      ${[0, 1, 2, 3, 4, 5]
        .map((i) => `<line x1='20' y1='${80 + i * 26}' x2='340' y2='${80 + i * 26}' stroke='#eadfb8' stroke-width='1'/>`)
        .join("")}
      <text x='20' y='44' font-family='Segoe UI, sans-serif' font-size='18' font-weight='700' fill='#3730a3'>${title}</text>
      ${lines
        .map((ln, i) => `<text x='22' y='${98 + i * 26}' font-family='Comic Sans MS, cursive' font-size='15' fill='#334155'>${ln}</text>`)
        .join("")}
      <text x='250' y='225' font-family='Segoe UI, sans-serif' font-size='11' fill='#94a3b8'>student upload</text>
    </svg>`
  );

const SampleDoubtsData = [
  {
    id: 101,
    studentName: "Sneha Patel",
    rollNo: "22CSE015",
    branch: "Ameerpet",
    class: "Class2",
    section: "SEC II-A",
    subject: "Operating Systems",
    chapter: "Deadlocks",
    category: "Conceptual",
    mobile: "9876543210",
    views: 14,
    isPublic: true,
    title: "What is a deadlock? How to avoid it?",
    description:
      "I understand processes wait for each other in deadlock, but how do we detect and prevent it?",
    images: [
      noteImage("Deadlock — my notes", [
        "P1 → holds R1, wants R2",
        "P2 → holds R2, wants R1",
        "circular wait?? how to break",
        "is banker's algo the answer?",
      ]),
    ],
    status: DOUBT_STATUS.PENDING,
    assignedFaculty: "Mr. Anil Verma",
    createdAt: "2026-06-11T07:50:00",
    answer: null,
  },
  {
    id: 102,
    studentName: "Karthik Reddy",
    rollNo: "22BPC042",
    branch: "Dilsukhnagar",
    class: "Class1",
    section: "SEC I-B",
    subject: "Botany",
    chapter: "Plant Anatomy",
    category: "Diagram",
    mobile: "9701122334",
    views: 6,
    isPublic: false,
    title: "Difference between monocot and dicot stem",
    description:
      "I can't clearly tell apart the vascular bundle arrangement in the diagram. Can you explain with a labelled figure?",
    images: [
      noteImage("Monocot vs Dicot stem", [
        "monocot → scattered bundles",
        "dicot → ring arrangement",
        "which is which in the figure?",
        "please label vascular bundle",
      ]),
    ],
    status: DOUBT_STATUS.REVIEWING,
    assignedFaculty: "Ms. Priya Sharma",
    createdAt: "2026-06-11T05:30:00",
    answer: null,
  },
  {
    id: 103,
    studentName: "Arjun Nair",
    rollNo: "22MPC008",
    branch: "Kukatpally",
    class: "Class2",
    section: "SEC II-A",
    subject: "Mathematics",
    chapter: "Integration",
    category: "Conceptual",
    mobile: "9988776655",
    views: 38,
    isPublic: true,
    title: "When to use integration by parts vs substitution",
    description:
      "How do I decide which method to apply when I see an integral? Is there a quick rule?",
    images: [],
    status: DOUBT_STATUS.SOLVED,
    assignedFaculty: "Mr. Anil Verma",
    createdAt: "2026-06-10T11:40:00",
    answer: {
      text:
        "Use the ILATE rule for integration by parts (Inverse, Log, Algebraic, Trig, Exponential). Use substitution when you can spot a function and its derivative together.",
      answeredBy: "Mr. Anil Verma",
      answeredAt: "2026-06-10T12:15:00",
    },
  },
  {
    id: 104,
    studentName: "Meghana Rao",
    rollNo: "22BPC019",
    branch: "Dilsukhnagar",
    class: "Class1",
    section: "SEC I-A",
    subject: "Chemistry",
    chapter: "Chemical Bonding",
    category: "Conceptual",
    mobile: "9123456780",
    views: 9,
    isPublic: false,
    title: "Why is water polar but CO2 non-polar?",
    description:
      "Both have polar bonds, so why is the net dipole moment zero only for CO2?",
    images: [],
    status: DOUBT_STATUS.PENDING,
    assignedFaculty: "Mrs. Sunitha Rao",
    createdAt: "2026-06-11T08:10:00",
    answer: null,
  },
  {
    id: 105,
    studentName: "Divya Krishnan",
    rollNo: "22MPC031",
    branch: "Kukatpally",
    class: "Class1",
    section: "SEC I-B",
    subject: "Physics",
    chapter: "Thermodynamics",
    category: "Conceptual",
    mobile: "9090807060",
    views: 52,
    isPublic: true,
    title: "Difference between isothermal and adiabatic process",
    description:
      "In which process does temperature stay constant, and how does the PV graph differ?",
    images: [],
    status: DOUBT_STATUS.SOLVED,
    assignedFaculty: "Dr. Ramesh Kumar",
    createdAt: "2026-06-09T09:20:00",
    answer: {
      text:
        "Isothermal → temperature constant (slow process, ΔT = 0), PV = constant. Adiabatic → no heat exchange (fast process, Q = 0), the adiabatic curve is steeper than the isothermal on a PV diagram.",
      answeredBy: "Dr. Ramesh Kumar",
      answeredAt: "2026-06-09T10:05:00",
    },
  },
  {
    id: 106,
    studentName: "Rahul Menon",
    rollNo: "22BPC077",
    branch: "Madhapur",
    class: "Class2",
    section: "SEC II-A",
    subject: "Chemistry",
    chapter: "Organic Basics",
    category: "Definition",
    mobile: "9345612780",
    views: 27,
    isPublic: true,
    title: "What is Markovnikov's rule with an example?",
    description:
      "Please explain Markovnikov's rule for the addition of HX to an alkene with a simple example.",
    images: [],
    status: DOUBT_STATUS.SOLVED,
    assignedFaculty: "Mrs. Sunitha Rao",
    createdAt: "2026-06-08T14:10:00",
    answer: {
      text:
        "Markovnikov's rule: when HX adds to an unsymmetrical alkene, H attaches to the carbon with more hydrogens, and X to the carbon with fewer. e.g. propene + HBr → 2-bromopropane (major product).",
      answeredBy: "Mrs. Sunitha Rao",
      answeredAt: "2026-06-08T15:00:00",
    },
  },
];

export default SampleDoubtsData;
